/**
 * @module planejamento/label/planejamentolabelediting
 */

import { Plugin } from 'ckeditor5/src/core';
import { DowncastWriter, Element } from 'ckeditor5/src/engine';
import { toWidget, viewToModelPositionOutsideModelElement, Widget } from 'ckeditor5/src/widget';
import PlanejamentoEditing from '../planejamentoediting';
import { Tema, Meta } from '../models';
import { getPlanejamentoLabelStr } from './utils';

const PLANEJAMENTO = 'planejamento';
const PLANEJAMENTO_LABEL = 'planejamentoLabel';

/**
 * The planejamentoLabel editing.
 *
 * Introduces the `'planejamentoLabel'` model element.
 *
 * @extends {Plugin}
 */
export default class PlanejamentoLabelEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'PlanejamentoLabelEditing';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ PlanejamentoEditing, Widget ];
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );
		/**
		 * @type {WeakMap<Meta, Array<Element>>}
		 */
		this._planejamentoLabels = new WeakMap();
		this._config = editor.config.get( PLANEJAMENTO ) || {};
		/**
		 * @type {Map<number, Tema>}
		 */
		this._temas = new Map();
		for ( const disp of this._config.temas ) {
			this._temas.set( disp.codigo, disp );
		}
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;
		this._defineSchema();
		this._defineConverters();

		editor.editing.mapper.on(
			'viewToModelPosition',
			viewToModelPositionOutsideModelElement( editor.model, viewElement => viewElement.hasClass( PLANEJAMENTO_LABEL ) )
		);
	}

	/**
	 * @private
	 */
	_defineSchema() {
		const schema = this.editor.model.schema;
		schema.register( PLANEJAMENTO_LABEL, {
			allowWhere: '$text',
			allowAttributesOf: '$text',
			isInline: true,
			isObject: true
		} );
	}

	/**
	 * @private
	 */
	_defineConverters() {
		const conversion = this.editor.conversion;
		conversion.for( 'upcast' ).elementToElement( {
			view: {
				name: 'span',
				classes: [ PLANEJAMENTO_LABEL ]
			},
			model: PLANEJAMENTO_LABEL
		} );

		conversion.for( 'dataDowncast' )
			.elementToElement( {
				model: PLANEJAMENTO_LABEL,
				view: ( modelItem, conversionApi ) => this._createPlanejamentoLabelViewElement( modelItem, conversionApi.writer, false )
			} );

		conversion.for( 'editingDowncast' )
			.elementToElement( {
				model: PLANEJAMENTO_LABEL,
				view: ( modelItem, conversionApi ) => toWidget( this._createPlanejamentoLabelViewElement( modelItem, conversionApi.writer, true ), conversionApi.writer )
			} )
			.add( dispatcher => {
				dispatcher.on( `attribute:${ PLANEJAMENTO }:${ PLANEJAMENTO }`, ( evt, data, conversionApi ) => {
					/**
					 * @type {Meta}
					 */
					const est = data.attributeNewValue;
					const labels = this._planejamentoLabels.get( data.attributeOldValue ) || [];
					this._planejamentoLabels.delete( data.attributeOldValue );
					this._planejamentoLabels.set( est, labels );
					for ( let idx = 0; idx < labels.length; idx++ ) {
						if ( labels[ idx ] ) {
							const disp = this._temas.get( est.tema );
							labels[ idx ].innerHTML = getPlanejamentoLabelStr( disp );
						} else {
							labels.splice( idx, 1 );
						}
					}
				} );
			} );
	}

	/** Creates a view element representing the planejamentoLabel.
	 *
	 *	<span class="planejamentoLabel"></span>
	 *
	 * @private
	 * @param {Element} modelItem
	 * @param {DowncastWriter} viewWriter
	 * @param {Boolean} [withLabel] Default false
	 * @returns
	 */
	_createPlanejamentoLabelViewElement( modelItem, viewWriter, withLabel = false ) {
		const temas = this._temas;
		const planejamentoLabels = this._planejamentoLabels;
		const cointainerView = viewWriter.createContainerElement( 'span', {
			class: PLANEJAMENTO_LABEL
		}, {
			isAllowedInsideAttributeElement: true
		} );

		if ( withLabel ) {
			const planejamentoLabelView = viewWriter.createRawElement( 'span', {
				class: PLANEJAMENTO_LABEL
			}, function ( domElement ) {

				const parent = modelItem.findAncestor( PLANEJAMENTO );
				if ( parent ) {
					const est = parent.getAttribute( PLANEJAMENTO );
					const disp = temas.get( est.tema );

					if ( !planejamentoLabels.has( est ) ) {
						planejamentoLabels.set( est, [] );
					}
					planejamentoLabels.get( est ).push( domElement );

					domElement.innerHTML = getPlanejamentoLabelStr( disp );
				} else {
					domElement.innerHTML = '<b>INVÁLIDO</b>';
				}
			} );

			viewWriter.insert( viewWriter.createPositionAt( cointainerView, 0 ), planejamentoLabelView );
		}

		return cointainerView;
	}
}
